<template>
  <div class="active-course-tile">
    <div class="active-course-tile__overlay"></div>
    <router-link
      class="active-course-tile__link-wrapper"
      :to="$buildContentLink('course', course)"
    >
      <div class="active-course-tile__tile">
        <div class="active-course-tile__tile-top">
          <div class="active-course-tile__instructor-wrapper">
            <img
              class="active-course-tile__instructor-img"
              :src="getInstructorImage(course)"
              :alt="getInstructor(course)"
            />
          </div>
          <div class="active-course-tile__info-wrapper">
            <div
              v-if="getTimeInDaysAgo(course)"
              class="dashboard__badge active-course-tile__course-last-activity"
            >
              {{ getTimeInDaysAgo(course) }}
            </div>
            <div class="active-course-tile__names-wrapper">
              <p class="active-course-tile__name">
                {{ truncateTitle(course.name) }}
              </p>
              <p class="active-course-tile__instructor-name">
                {{ getInstructor(course) }}
              </p>
            </div>
          </div>
        </div>
        <div class="active-course-tile__tile-bottom">
          <div
            v-if="hasTrackableContent(course)"
            class="dashboard__progress-wrapper"
          >
            <div
              class="dashboard__progress-bar"
              :style="`width: ${getProgress(course)}%`"
            ></div>
          </div>
          <div v-else class="dashboard__progress-no-trackable-container">
            <div class="dashboard__progress-no-trackable-text">
              Started
            </div>
          </div>
          <div class="dashboard__icons-wrapper">
            <TrashIcon
              class="dashboard__action-icon dashboard__icon dashboard__icon--trash"
              @click="removeActiveCourse($event, course)"
            />
            <PlayIcon class="dashboard__action-icon dashboard__icon--play" />
          </div>
        </div>
      </div>
    </router-link>
    <ActionLoader
      v-if="isDeletingActiveCourse"
      class="dashboard-item__action-loader active-course-tile__action-loader"
    />
  </div>
</template>

<script>
import { defineComponent, ref } from '@vue/composition-api'
import { formatDistance } from 'date-fns'
import { truncateText, getEntities, getInstructorImg } from '@/helpers'
import { PlayIcon, TrashIcon } from 'vue-feather-icons'
import ActionLoader from '@/components/shared/ActionLoader.vue'

export default defineComponent({
  name: 'ActiveCourseTile',
  components: { PlayIcon, TrashIcon, ActionLoader },
  props: {
    course: {
      type: Object,
      required: true,
    },
  },
  setup(props, { root }) {
    const isDeletingActiveCourse = ref(false)
    const getInstructor = course => getEntities.getInstructorName(course)
    const getInstructorImage = course => {
      const url =
        course?.instructors[0]?.profile_img_url ??
        'https://s3.amazonaws.com/ine-vegas/production/images/instructors/INE-Instructor.png'
      return getInstructorImg(url)
    }
    const truncateTitle = value => truncateText(value, 90)
    const getProgress = course => {
      const progress = course?.user_status?.progress ?? course?.progress ?? 0
      return progress > 0 ? Math.floor(progress * 100) : 0
    }
    const getTimeInDaysAgo = course => {
      const time = course?.user_status?.last_activity_date ?? ''
      return time && time !== 'None'
        ? `${formatDistance(new Date(), new Date(time))} ago`
        : ''
    }
    const removeActiveCourse = async ($event, course) => {
      $event.preventDefault()
      isDeletingActiveCourse.value = true
      await root.$store.dispatch('courses/removeActiveCourse', course.id)
      await root.$store.dispatch('courses/getActiveCourses')
      root.sendMixpanelEvent(
        root.eventsConstants.CONTENT_COURSE_ACTIVE_REMOVED,
        {
          course_id: course.id,
          course_name: course.name,
          trigger: 'userDashboard',
          ...course,
        }
      )
      isDeletingActiveCourse.value = false
    }
    const hasTrackableContent = course =>
      course?.user_status?.has_trackable_content

    return {
      getInstructor,
      getInstructorImage,
      truncateTitle,
      getProgress,
      getTimeInDaysAgo,
      removeActiveCourse,
      hasTrackableContent,
      isDeletingActiveCourse,
    }
  },
})
</script>

<style lang="scss">
.active-course-tile {
  $this: &;
  position: relative;
  min-height: 226px;
  border-radius: 10px;
  margin-bottom: 20px;
  display: flex;
  width: 100%;
  z-index: 1;
  background: var(--glass-mid-gray);
  @include mobile {
    min-height: 159px;
  }
  @include tablet {
    max-width: 800px;
  }
  &:hover {
    :after {
      opacity: 1;
      transition: 0.2s;
    }
    #{$this}__overlay {
      opacity: 1;
    }
    #{$this}__learning-area {
      color: var(--white);
    }
    #{$this}__vendor-name::before {
      left: -20px;
      width: 10px;
    }
    #{$this}__progress-wrapper {
      display: block;
    }
  }
  &__overlay {
    position: absolute;
    height: 100%;
    width: 100%;
    background-color: rgba(0, 0, 0, 0.2);
    right: 0;
    top: 0;
    bottom: 0;
    left: 0;
    z-index: -1;
    opacity: 0;
    -webkit-backdrop-filter: saturate(100%) blur(6px);
    backdrop-filter: saturate(100%) blur(6px);
    transition: 0.2s all ease;
    border-radius: 10px;
    min-height: 226px;
    @include mobile {
      min-height: 159px;
    }
  }
  &__link-wrapper {
    width: 100%;
  }
  &__tile {
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    padding: 20px;
    width: 100%;
    border-radius: 10px;
    min-height: 226px;
    @include mobile {
      min-height: 159px;
    }
    &:hover {
      background: var(--glass-extra-light);
      .dashboard__icon--play {
        border-color: var(--white);
      }
    }
  }
  &__tile-top,
  &__tile-bottom {
    z-index: 1;
  }
  &__tile-top {
    display: flex;
    margin-bottom: 20px;
  }

  &__tile-bottom {
    display: flex;
    align-items: center;
    width: 100%;
  }
  &__info-wrapper {
    display: flex;
    padding-left: 20px;
    flex-direction: column;
    width: 100%;
    @include mobile {
      flex-direction: row-reverse;
    }
  }
  &__names-wrapper {
    flex: 1 0;
  }
  &__names {
    @include mobile {
      width: 100%;
    }
  }
  &__name {
    font-size: rem(16);
    line-height: 23px;
    font-weight: 500;
    color: var(--white);
    font-family: var(--font-book);
    margin-bottom: 10px;
    @include mobile {
      margin-bottom: 0;
    }
    @include tablet {
      font-size: rem(18);
      line-height: 24px;
    }
  }

  &__course-length {
    text-transform: uppercase;
  }
  &__instructor-img {
    border-radius: 100%;
    max-height: 50px;
  }
  &__instructor-name {
    font-size: rem(16);
    line-height: 24px;
    color: var(--light-blue-gray);
    margin: 0;
    @include tablet {
      font-size: rem(16);
      line-height: 24px;
    }
  }
  &__course-last-activity {
    margin-bottom: 10px;
  }
  &__action-loader {
    border-radius: 10px !important;
  }
}
</style>
